import {
    Context,
    createContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'

interface IToggleableContext<T> {
    readonly value: boolean
    readonly toggle: (value?: boolean, meta?: T) => void
    readonly meta?: T
    readonly url?: string
    setUrl: (newUrl: string) => void
}

function createToggleableContext<T>(
    defaultValue?: boolean,
    meta?: T,
    url?: string
): Context<IToggleableContext<T>> {
    let _globalValue = !!defaultValue
    let _url = url

    const ToggleableContext = createContext<IToggleableContext<T>>({
        get value() {
            return _globalValue
        },
        get meta() {
            return meta
        },
        get url() {
            return _url
        },
        toggle(value?: boolean) {
            _globalValue = value === undefined ? !_globalValue : value // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
        },
        setUrl(newUrl: string) {
            _url = newUrl
        },
    })

    return ToggleableContext
}

function useToggleableContext<T>(
    isOpen?: boolean,
    meta?: T,
    url?: string
): IToggleableContext<T> {
    const [open, setOpen] = useState(!!isOpen)
    const metaValue = useRef(meta)
    const [urlValue, setUrlValue] = useState(url)

    const context = useMemo(() => {
        return {
            value: open,
            meta: metaValue.current,
            toggle(value, meta?: T) {
                if (value === undefined) {
                    setOpen(!open)
                } else if (typeof value === 'boolean') {
                    setOpen(value)
                } else {
                    setOpen((o) => !o)
                }

                metaValue.current = meta
            },
            url: urlValue,
            setUrl(newUrl: string) {
                setUrlValue(newUrl)
            },
        } satisfies IToggleableContext<T>
    }, [open])

    useEffect(() => {
        setOpen(!!isOpen)
    }, [isOpen])

    useEffect(() => {
        setUrlValue(url)
    }, [url])

    return context
}

export { useToggleableContext, createToggleableContext }
